import { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Link } from "react-router-dom";
import { locations, primaryLocation } from "@/lib/locations-data";
import { siteConfig } from "@/lib/site-config";
import "leaflet/dist/leaflet.css";

const LocationMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markersRef = useRef<Record<string, L.Marker>>({});
  const [activeSlug, setActiveSlug] = useState<string>(primaryLocation.slug);
  /** Leaflet and the OSM tiles are ~150 kB; hold them until the map is near. */
  const [mapInView, setMapInView] = useState(false);

  useEffect(() => {
    const el = mapRef.current;
    if (!el) return;
    if (!("IntersectionObserver" in window)) {
      setMapInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setMapInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!mapInView) return;

    const initMap = async () => {
      const L = await import("leaflet");

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
      }

      if (!mapRef.current) return;

      const map = L.map(mapRef.current, {
        zoomControl: true,
        scrollWheelZoom: false,
      }).setView([primaryLocation.lat, primaryLocation.lng], 9);

      mapInstanceRef.current = map;

      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: "&copy; OpenStreetMap contributors",
        maxZoom: 19,
        detectRetina: true,
      }).addTo(map);

      // 50-mile service radius (80,467 meters)
      L.circle([primaryLocation.lat, primaryLocation.lng], {
        radius: 80467,
        color: "hsl(0, 72%, 37%)",
        fillColor: "hsl(0, 72%, 37%)",
        fillOpacity: 0.07,
        weight: 2,
        dashArray: "8 4",
      }).addTo(map);

      const primaryIcon = L.divIcon({
        className: "custom-marker-primary",
        html: `<div style="background:hsl(0,82%,52%);width:36px;height:36px;border-radius:50%;border:3px solid white;box-shadow:0 4px 12px rgba(0,0,0,0.35);display:flex;align-items:center;justify-content:center;">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/></svg>
        </div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 18],
      });

      const cityIcon = L.divIcon({
        className: "custom-marker-city",
        html: `<div style="background:hsl(0,0%,15%);width:20px;height:20px;border-radius:50%;border:2px solid white;box-shadow:0 2px 6px rgba(0,0,0,0.25);"></div>`,
        iconSize: [20, 20],
        iconAnchor: [10, 10],
      });

      // Markers go onto the map as one layer group rather than ten individual
      // addTo() calls. Adding them one at a time interleaves a DOM insertion
      // with Leaflet's own geometry reads, which Chrome reports as a forced
      // reflow per marker; the group does it in a single pass.
      const labels = new Map<string, string>();
      const markers = locations.map((loc) => {
        const icon = loc.isPrimary ? primaryIcon : cityIcon;
        const label = `${loc.name}, ${loc.state}${loc.isPrimary ? " — headquarters" : ""}`;
        labels.set(loc.slug, label);
        const marker = L.marker([loc.lat, loc.lng], { icon, title: label, alt: label }).bindPopup(
          `<div style="padding:8px;font-family:sans-serif;min-width:180px;">
              <strong style="font-size:14px;color:hsl(0,72%,37%);">${loc.name}, ${loc.state}</strong>
              <p style="color:#666;font-size:12px;margin:4px 0 6px;">${loc.region}${loc.isPrimary ? " · HQ" : ""}</p>
              <a href="/locations/${loc.slug}" style="color:hsl(0,72%,37%);font-weight:600;font-size:12px;text-decoration:none;">View location →</a>
            </div>`
        );
        markersRef.current[loc.slug] = marker;
        return marker;
      });

      L.layerGroup(markers).addTo(map);

      // getElement() only returns a node once the marker is on the map, so the
      // accessible names are applied after the group has been added.
      locations.forEach((loc) => {
        markersRef.current[loc.slug]
          ?.getElement()
          ?.setAttribute("aria-label", labels.get(loc.slug) ?? loc.name);
      });
    };

    initMap();

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [mapInView]);

  const handleLocationClick = (slug: string, lat: number, lng: number) => {
    setActiveSlug(slug);
    if (mapInstanceRef.current) {
      const zoom = slug === primaryLocation.slug ? 10 : 12;
      mapInstanceRef.current.flyTo([lat, lng], zoom, { duration: 1 });
      setTimeout(() => {
        markersRef.current[slug]?.openPopup();
      }, 800);
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden border border-border shadow-lg bg-card">
      <div className="bg-primary px-5 py-3 flex items-center gap-2">
        <MapPin className="h-4 w-4 text-primary-foreground" />
        <span className="font-heading font-bold text-primary-foreground text-sm tracking-wide">
          Service Area — {siteConfig.address.serviceRadius} Radius from {siteConfig.address.city},{" "}
          {siteConfig.address.state}
        </span>
      </div>

      <div className="flex flex-col lg:flex-row">
        <div className="w-full lg:w-[35%] lg:border-r border-border overflow-y-auto max-h-[350px] lg:max-h-[520px] scrollbar-hide">
          {locations.map((loc) => (
            <div
              key={loc.slug}
              className={`w-full px-5 py-3 flex items-center justify-between border-b border-border transition-all ${
                activeSlug === loc.slug
                  ? "bg-primary/10 border-l-4 border-l-primary"
                  : "hover:bg-muted/50 border-l-4 border-l-transparent"
              }`}
            >
              <button
                type="button"
                onClick={() => handleLocationClick(loc.slug, loc.lat, loc.lng)}
                aria-pressed={activeSlug === loc.slug}
                className="flex flex-1 items-center gap-2.5 text-left min-w-0"
              >
                {loc.isPrimary ? (
                  <span className="h-3 w-3 rounded-full bg-secondary shrink-0 ring-2 ring-secondary/30" />
                ) : (
                  <span className="h-2.5 w-2.5 rounded-full bg-primary shrink-0" />
                )}
                <span className="block">
                  <span className={`block font-heading font-bold text-sm tracking-tight ${activeSlug === loc.slug ? "text-primary" : "text-foreground"}`}>
                    {loc.name}, {loc.state}
                  </span>
                  <span className="block text-muted-foreground text-[11px]">
                    {loc.region}{loc.isPrimary ? " · HQ" : ""}
                  </span>
                </span>
                <span className="sr-only">— show on map</span>
              </button>
              <Link
                to={`/locations/${loc.slug}`}
                className="text-xs font-semibold text-primary hover:text-primary/80 whitespace-nowrap"
              >
                View<span className="sr-only"> {loc.name} location page</span> →
              </Link>
            </div>
          ))}
        </div>

        <div className="w-full lg:w-[65%] h-[400px] lg:h-[520px] relative" style={{ zIndex: 0 }}>
          <div ref={mapRef} className="w-full h-full" style={{ zIndex: 0 }} />
        </div>
      </div>
    </div>
  );
};

export default LocationMap;
